import { Plus, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { useToast } from '@/components/shared/Toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { mutationErrorMessage, normalizeApiError } from '@/features/common/apiError';

import { useCreateEnvironmentMutation } from './reportsApi';
import type { EnvironmentView } from './types';

/**
 * Field-level errors from §8.2's validation list; all copy is verbatim (§17).
 */
const NAME_REQUIRED = 'Enter a name.';
const WHOLE_NUMBER = 'Enter a whole number of 0 or more.';
const SERVICE_REQUIRED = 'Enter a service name.';
const SERVICE_PATTERN = 'Use lowercase letters, numbers, and hyphens.';
const SERVICE_KEY_PATTERN = /^[a-z0-9-]+$/;
const NON_NEGATIVE_INTEGER = /^\d+$/;

interface LimitRow {
  service: string;
  max: string;
}

interface RowErrors {
  service?: string;
  max?: string;
}

interface WizardErrors {
  name?: string;
  threshold?: string;
  rows: RowErrors[];
  /** Unmapped server error shown as an inline alert at the top of the step (D5). */
  form?: string;
}

const NO_ERRORS: WizardErrors = { rows: [] };

/**
 * Create-environment wizard (design §8.2) — one dialog, two steps:
 * step 1 the identity decision (Name), step 2 the dedup/quota policy. Inputs
 * carry the SERVER defaults (threshold 10, storage 2), so an untouched wizard
 * submits exactly the server defaults (D3: values shown are values sent).
 */
export function NewEnvironmentWizard({
  open,
  onOpenChange,
  reportProjectId,
  onProvisioned,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reportProjectId: string;
  /** Handed the create response — the plaintext key goes straight to the parent's display-once state (P6). */
  onProvisioned: (environment: EnvironmentView, apiKey: string) => void;
}) {
  const toast = useToast();
  const [createEnvironment, createState] = useCreateEnvironmentMutation();

  const [step, setStep] = useState<1 | 2>(1);
  const [name, setName] = useState('');
  const [threshold, setThreshold] = useState('10');
  const [limits, setLimits] = useState<LimitRow[]>([{ service: 'storage', max: '2' }]);
  const [errors, setErrors] = useState<WizardErrors>(NO_ERRORS);

  const nameRef = useRef<HTMLInputElement>(null);
  const thresholdRef = useRef<HTMLInputElement>(null);
  const rowRefs = useRef<Array<{ service: HTMLInputElement | null; max: HTMLInputElement | null }>>(
    [],
  );

  // Fresh wizard per open (Cancel discards; Back preserves within a session).
  useEffect(() => {
    if (open) {
      setStep(1);
      setName('');
      setThreshold('10');
      setLimits([{ service: 'storage', max: '2' }]);
      setErrors(NO_ERRORS);
    }
  }, [open]);

  function validateStep1(): boolean {
    if (!name.trim()) {
      setErrors({ name: NAME_REQUIRED, rows: [] });
      nameRef.current?.focus();
      return false;
    }
    setErrors({ rows: [] });
    return true;
  }

  function focusFirstInvalidRow(rowError: RowErrors, index: number) {
    const refs = rowRefs.current[index];
    if (rowError.service) {
      refs?.service?.focus();
    } else {
      refs?.max?.focus();
    }
  }

  function validateStep2(): boolean {
    const next: WizardErrors = { rows: [] };

    if (!NON_NEGATIVE_INTEGER.test(threshold.trim())) {
      next.threshold = WHOLE_NUMBER;
    }

    limits.forEach((row) => {
      const rowError: RowErrors = {};
      if (!row.service.trim()) {
        rowError.service = SERVICE_REQUIRED;
      } else if (!SERVICE_KEY_PATTERN.test(row.service.trim())) {
        rowError.service = SERVICE_PATTERN;
      }
      if (!NON_NEGATIVE_INTEGER.test(row.max.trim())) {
        rowError.max = WHOLE_NUMBER;
      }
      next.rows.push(rowError);
    });

    const rowsInvalid = next.rows.some((r) => Boolean(r.service ?? r.max));
    if (next.threshold !== undefined || rowsInvalid) {
      setErrors(next);
      if (next.threshold) {
        thresholdRef.current?.focus();
      } else {
        const index = next.rows.findIndex((r) => r.service ?? r.max);
        focusFirstInvalidRow(next.rows[index], index);
      }
      return false;
    }

    setErrors({ rows: [] });
    return true;
  }

  async function submit() {
    const maxResources: Record<string, number> = {};
    limits.forEach((row) => {
      maxResources[row.service.trim()] = Number(row.max.trim());
    });

    try {
      const result = await createEnvironment({
        reportProjectId,
        name: name.trim(),
        duplicateThreshold: Number(threshold.trim()),
        maxResources,
      }).unwrap();

      if (result?.apiKey && result.environment) {
        toast.success('Environment created.');
        onProvisioned(result.environment, result.apiKey);
        // P6: the plaintext is now ONLY in the parent's modal state — drop the
        // RTK Query mutation cache entry that still holds the response.
        createState.reset();
        onOpenChange(false);
      } else {
        toast.error("Couldn't create environment.");
      }
    } catch (error) {
      const normalized = normalizeApiError(error);
      if (normalized.status === 422 && normalized.serverMessage) {
        // Zod field errors from the server map to the inline alert of the
        // current step; the client-side rules above already cover the common
        // cases (§8.2: unmapped errors render as an inline alert).
        setErrors((prev) => ({ ...prev, form: normalized.serverMessage }));
      } else {
        toast.error(mutationErrorMessage(error, "Couldn't create environment."));
      }
    }
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (createState.isLoading) {
      return;
    }
    if (step === 1) {
      if (validateStep1()) {
        setStep(2);
      }
      return;
    }
    if (validateStep2()) {
      void submit();
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !createState.isLoading && onOpenChange(next)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New environment</DialogTitle>
          <div className="mt-1 flex items-center gap-2">
            <p className="text-xs text-muted-foreground">Step {step} of 2</p>
            <span className="flex gap-1" aria-hidden="true">
              <span className={`h-1 w-8 rounded ${step === 1 ? 'bg-primary' : 'bg-muted'}`} />
              <span className={`h-1 w-8 rounded ${step === 2 ? 'bg-primary' : 'bg-muted'}`} />
            </span>
          </div>
        </DialogHeader>

        <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4">
          {errors.form ? (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
            >
              {errors.form}
            </div>
          ) : null}

          {step === 1 ? (
            <div className="space-y-2">
              <Label htmlFor="env-name">Name</Label>
              <Input
                id="env-name"
                ref={nameRef}
                placeholder="e.g. Production web app"
                value={name}
                maxLength={200}
                aria-invalid={Boolean(errors.name)}
                aria-describedby={errors.name ? 'env-name-error' : 'env-name-help'}
                onChange={(event) => setName(event.target.value)}
              />
              {errors.name ? (
                <p id="env-name-error" role="alert" className="text-xs text-destructive">
                  {errors.name}
                </p>
              ) : (
                <p id="env-name-help" className="text-sm text-muted-foreground">
                  A label for this environment. Each environment gets its own webhook and API key.
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm font-medium">Limits</p>

              <div className="space-y-2">
                <Label htmlFor="env-threshold">Duplicate threshold</Label>
                <Input
                  id="env-threshold"
                  ref={thresholdRef}
                  type="number"
                  min={0}
                  step={1}
                  className="w-20"
                  value={threshold}
                  aria-invalid={Boolean(errors.threshold)}
                  aria-describedby={errors.threshold ? 'env-threshold-error' : 'env-threshold-help'}
                  onChange={(event) => setThreshold(event.target.value)}
                />
                {errors.threshold ? (
                  <p id="env-threshold-error" role="alert" className="text-xs text-destructive">
                    {errors.threshold}
                  </p>
                ) : (
                  <p id="env-threshold-help" className="text-sm text-muted-foreground">
                    How many duplicate reports an issue accepts before new ones are rejected as
                    already known. 0 means unlimited.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium" id="env-limits-label">
                  Resource limits per report
                </p>
                <div className="space-y-2">
                  {limits.map((row, index) => {
                    const rowError = errors.rows[index] ?? {};
                    return (
                      <div key={index} className="flex items-start gap-2">
                        <div className="w-40">
                          <Label htmlFor={`env-limit-service-${index}`} className="sr-only">
                            Service name
                          </Label>
                          <Input
                            id={`env-limit-service-${index}`}
                            ref={(el) => {
                              rowRefs.current[index] = rowRefs.current[index] ?? {
                                service: null,
                                max: null,
                              };
                              rowRefs.current[index].service = el;
                            }}
                            placeholder="storage"
                            value={row.service}
                            maxLength={50}
                            aria-invalid={Boolean(rowError.service)}
                            onChange={(event) =>
                              setLimits((prev) =>
                                prev.map((r, i) =>
                                  i === index ? { ...r, service: event.target.value } : r,
                                ),
                              )
                            }
                          />
                        </div>
                        <div className="w-20">
                          <Label htmlFor={`env-limit-max-${index}`} className="sr-only">
                            Maximum
                          </Label>
                          <Input
                            id={`env-limit-max-${index}`}
                            ref={(el) => {
                              rowRefs.current[index] = rowRefs.current[index] ?? {
                                service: null,
                                max: null,
                              };
                              rowRefs.current[index].max = el;
                            }}
                            type="number"
                            min={0}
                            step={1}
                            value={row.max}
                            aria-invalid={Boolean(rowError.max)}
                            onChange={(event) =>
                              setLimits((prev) =>
                                prev.map((r, i) =>
                                  i === index ? { ...r, max: event.target.value } : r,
                                ),
                              )
                            }
                          />
                        </div>
                        {index > 0 ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-10 w-10 shrink-0 text-muted-foreground hover:text-foreground"
                            aria-label="Remove service limit"
                            onClick={() => setLimits((prev) => prev.filter((_, i) => i !== index))}
                          >
                            <X className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        ) : null}
                        {(rowError.service ?? rowError.max) ? (
                          <p role="alert" className="mt-0.5 text-xs text-destructive">
                            {rowError.service ?? rowError.max}
                          </p>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
                <Button
                  type="button"
                  variant="link"
                  className="h-auto px-0"
                  onClick={() => setLimits((prev) => [...prev, { service: '', max: '0' }])}
                >
                  <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
                  Add a service limit
                </Button>
                <p className="text-sm text-muted-foreground">
                  Maximum attachments per report, by service name. Reports can&apos;t exceed these
                  limits.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            {step === 1 ? (
              <>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit">Continue</Button>
              </>
            ) : (
              <>
                <Button type="button" variant="outline" onClick={() => setStep(1)}>
                  Back
                </Button>
                <Button type="submit" disabled={createState.isLoading}>
                  {createState.isLoading ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Creating…
                    </>
                  ) : (
                    'Create environment'
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
